import React, { useState, useEffect } from 'react';
import './FormModal.css';

export default function FormModal({ title, fields, onSave, onClose, initialData, existingItems = [] }) {
  const [form, setForm] = useState(() => {
    const init = {};
    fields.forEach((f) => {
      if (initialData && initialData[f.key] !== undefined) {
        init[f.key] = initialData[f.key];
      } else {
        init[f.key] = f.default ?? '';
      }
    });
    return init;
  });
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  });

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 200);
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
    } catch {
      setSaving(false);
    }
  };

  return (
    <div
      className={`form-modal__overlay ${closing ? 'form-modal__overlay--exit' : ''}`}
      onClick={handleClose}
    >
      <div
        className={`form-modal ${closing ? 'form-modal--exit' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="form-modal__header">
          <h3 className="form-modal__title">{title}</h3>
          <button className="form-modal__close" onClick={handleClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="form-modal__body">
          {fields.map((field) => (
            <div key={field.key} className="form-modal__group">
              <label className="form-modal__label">{field.label}</label>
              {field.type === 'textarea' ? (
                <textarea
                  className="form-modal__input form-modal__textarea"
                  value={form[field.key] || ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  placeholder={field.placeholder || field.label}
                  required={field.required}
                  rows={4}
                />
              ) : field.type === 'select' ? (
                <select
                  className="form-modal__input form-modal__select"
                  value={form[field.key] || ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  required={field.required}
                >
                  <option value="">Select {field.label}</option>
                  {field.options?.map((opt) => (
                    <option key={opt.value ?? opt} value={opt.value ?? opt}>
                      {opt.label ?? opt}
                    </option>
                  ))}
                </select>
              ) : field.type === 'combobox' ? (() => {
                const dynamicVals = field.optionsFrom
                  ? [...new Set(existingItems.map(item => item[field.optionsFrom]).filter(Boolean))]
                  : [];
                const allOptions = [...new Set([...(field.defaults || []), ...dynamicVals])];
                const listId = `combobox-${field.key}`;
                return (
                  <>
                    <input
                      list={listId}
                      className="form-modal__input"
                      value={form[field.key] || ''}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      placeholder={field.placeholder || `Type or select ${field.label.toLowerCase()}`}
                      required={field.required}
                    />
                    <datalist id={listId}>
                      {allOptions.map((opt) => (
                        <option key={opt} value={opt} />
                      ))}
                    </datalist>
                  </>
                );
              })()
              : field.type === 'color' ? (
                <div className="form-modal__color-wrap">
                  <input
                    type="color"
                    className="form-modal__color-picker"
                    value={form[field.key] || '#000000'}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                  />
                  <span className="form-modal__color-value">{form[field.key] || '#000000'}</span>
                </div>
              ) : field.type === 'number' ? (
                <input
                  type="number"
                  className="form-modal__input"
                  value={form[field.key] ?? ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  placeholder={field.placeholder || field.label}
                  required={field.required}
                  min={field.min}
                  max={field.max}
                />
              ) : (
                <input
                  type={field.type || 'text'}
                  className="form-modal__input"
                  value={form[field.key] || ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  placeholder={field.placeholder || field.label}
                  required={field.required}
                />
              )}
            </div>
          ))}

          <div className="form-modal__actions">
            <button type="button" className="form-modal__btn form-modal__btn--cancel" onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="form-modal__btn form-modal__btn--save" disabled={saving}>
              {saving ? 'Saving...' : initialData ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
